import { useTranslation } from "react-i18next";
import "../../../i18n";
import { MethodChip, Notice } from "../../../design/components";
import { METHOD_BY_CONNECTION } from "../../../domain/health";
import { useMe } from "../../../api/queries";
import { dateTime } from "../../../lib/format";
import type { Entity, ProbeStep } from "../../../api/types";
import { useConnection } from "../../hub/onboard/onboardApi";
import { AgentTokensPanel } from "./AgentTokensPanel";
import { DiscoveryPanel } from "./DiscoveryPanel";
import { MethodSettings } from "./MethodSettings";
import { ProbePanel } from "./ProbePanel";
import { PushPanel } from "./PushPanel";
import { FileIntakePanel } from "../connection/FileIntakePanel";
import styles from "./connection.module.css";

/** The same gate the API applies to connection writes (M2 ruling 23). */
const EDITORS = ["group_admin", "entity_admin"];

export function ConnectionTab({ entity }: { entity: Entity }) {
  const { t } = useTranslation();
  const connection = useConnection(entity.id);
  const me = useMe();
  const canEdit = EDITORS.includes(me.data?.role ?? "");

  if (connection.isError) {
    return (
      <Notice kind="fail">
        {t("companies.connection.loadError", "These connection settings could not be loaded.")}
      </Notice>
    );
  }
  if (!connection.data) return null;

  const card = connection.data;
  const lastProbe = card.lastProbe
    ? { at: card.lastProbe.at, ok: card.lastProbe.ok, steps: card.lastProbe.steps as ProbeStep[] }
    : null;

  return (
    <div className={styles.tab}>
      <div className={styles.head}>
        <MethodChip method={METHOD_BY_CONNECTION[card.method]} full />
        <span className={styles.meta}>
          {card.lastContactAt
            ? t("companies.connection.lastContact", "Last contact {{date}}", { date: dateTime(card.lastContactAt) })
            : t("companies.connection.noContactYet", "No contact yet")}
        </span>
      </div>

      <MethodSettings entity={entity} card={card} canEdit={canEdit} />
      <ProbePanel entityId={entity.id} lastProbe={lastProbe} canRun={canEdit} />
      {card.method === "file" || card.method === "push" ? null : (
        <DiscoveryPanel entity={entity} canRun={canEdit} />
      )}
      {card.method === "push" && card.push ? (
        <>
          <PushPanel push={card.push} lastContactAt={card.lastContactAt} />
          <AgentTokensPanel
            entityId={entity.id}
            silenceThresholdMinutes={entity.silenceThresholdMinutes}
            canManage={canEdit}
            kind="push"
          />
        </>
      ) : null}
      {card.method === "agent" ? (
        <AgentTokensPanel
          entityId={entity.id}
          silenceThresholdMinutes={entity.silenceThresholdMinutes}
          canManage={canEdit}
        />
      ) : null}
      <FileIntakePanel entity={entity} config={card.config} />
    </div>
  );
}
